import { useEffect, useState } from 'react';

export enum AppStorageKey {
  User = 'user',
  Entertainer = 'entertainer',
}

type AppStorageKeyProperties = {
  [AppStorageKey.User]: any;
  [AppStorageKey.Entertainer]: any;
};

export const AppStorage = {
  async getItem<Key extends AppStorageKey>(key: Key): Promise<AppStorageKeyProperties[Key]> {
    try {
      const value = localStorage.getItem(key);

      if (value) {
        return JSON.parse(value);
      }
    } catch (error) {
      console.error(`Error getting item from storage: ${error}`);
    }

    return undefined;
  },
  async setItem<Key extends AppStorageKey>(
    key: Key,
    value: AppStorageKeyProperties[Key],
  ): Promise<void> {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Error setting item in storage: ${e}`);
    }
  },
  async reset() {
    try {
      Object.entries(AppStorageKey).forEach(([key]) => {
        localStorage.removeItem(key);
        appStorageCache.delete(key as AppStorageKey);
      });
    } catch (e) {
      console.error(`Error clearing storage: ${e}`);
    }
  },
};

const appStorageCache = new Map<AppStorageKey, AppStorageKeyProperties[AppStorageKey]>();

export const useAppStorageValue = <Key extends AppStorageKey>(key: Key) => {
  const [value, setValue] = useState<AppStorageKeyProperties[Key] | undefined>(
    appStorageCache.get(key),
  );

  useEffect(() => {
    if (!value) {
      const fetchValue = async () => {
        const cachedValue = appStorageCache.get(key);

        if (!cachedValue) {
          const storedValue = await AppStorage.getItem(key);
          appStorageCache.set(key, storedValue);
          setValue(storedValue);
        }
      };

      fetchValue();
    }
  }, []);

  return value;
};
